import React, { useEffect, useState } from 'react';
import {
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Filter,
  Package,
  CheckCircle,
  XCircle,
  Eye,
  EyeIcon
} from 'lucide-react';
import { useAuth } from '../../../context/Auth';
import { useGlobalContext } from '../../../context/stateContext';
import Swal from 'sweetalert2';


const AdminItemReturnList = () => {
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();

  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Modal for approval details
  const [showModal, setShowModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [approvalNote, setApprovalNote] = useState("");
  const [status, setStatus] = useState("Pending Approval");

  // Bulk selection state
  const [selectedReturns, setSelectedReturns] = useState([]);

  const fetchPendingReturns = async (page, size, search, start, end, statusFilter) => {
    setLoading(true);
    setSelectedReturns([]);
    try {
      // API endpoint to get returns pending admin approval
      let url = `/inventory/admin/sale-item-return/?status=${statusFilter}&page=${page}&pageSize=${size}`;

      if (search) url += `&search=${search}`;
      if (start) url += `&start_date=${start}`;
      if (end) url += `&end_date=${end}`;

      const data = await requestWithAuth(url, "GET");
      setReturns(data?.results || []);
      setNextPage(data?.next);
      setPrevPage(data?.previous);
      setTotalPages(Math.ceil(data?.count / size));
    } catch (error) {
      console.error("Error fetching pending returns:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchPendingReturns(1, pageSize, searchQuery, startDate, endDate, status);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setStatus("Pending Approval");
    setCurrentPage(1);
    fetchPendingReturns(1, pageSize, "", "", "", "Pending Approval");
  };

  const handleApprovalAction = async (returnId, action) => {
    setActionLoading(returnId);
    try {
      const newStatus = action === 'approve' ? 'Approved' : 'Rejected';
      const payload = {
        status: newStatus,
        admin_note: approvalNote
      };

      await requestWithAuth(
        `/inventory/sale-item-return/${returnId}/`,
        "PUT",
        payload
      );

      Swal.fire({
        icon: 'success',
        title: `Return ${action === 'approve' ? 'approved' : 'rejected'} successfully`,
        showConfirmButton: false,
        timer: 1500
      })

      setShowModal(false);
      setSelectedReturn(null);
      setApprovalNote("");
      fetchPendingReturns(currentPage, pageSize, searchQuery, startDate, endDate, status);
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkApprovalAction = async (action) => {
    const itemsToProcess = selectedReturns.filter(item => item.status === "Pending Approval");
    if (itemsToProcess.length === 0) {
      Swal.fire('Info', 'No pending returns selected for this action.', 'info');
      return;
    }

    const actionText = action === 'approve' ? 'Approve' : 'Reject';
    const newStatus = action === 'approve' ? 'Approved' : 'Rejected';

    Swal.fire({
      icon: 'question',
      title: `Confirm Bulk ${actionText}`,
      text: `Are you sure you want to ${actionText.toLowerCase()} ${itemsToProcess.length} return request(s)?`,
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText}`,
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setActionLoading('bulk');
        let successCount = 0;
        let errorCount = 0;

        try {
          for (const item of itemsToProcess) {
            try {
              const payload = {
                status: newStatus,
                admin_note: ""
              };

              await requestWithAuth(
                `/inventory/sale-item-return/${item.id}/`,
                "PUT",
                payload
              );
              successCount++;
            } catch (err) {
              console.error(`Error processing return ${item.id}:`, err);
              errorCount++;
            }
          }

          Swal.fire({
            icon: errorCount === 0 ? 'success' : 'warning',
            title: `Bulk Action Completed`,
            text: `Successfully ${newStatus.toLowerCase()} ${successCount} request(s). ${errorCount > 0 ? "Failed: " + errorCount : ""}`,
            showConfirmButton: true
          });

          setSelectedReturns([]);
          fetchPendingReturns(currentPage, pageSize, searchQuery, startDate, endDate, status);
        } catch (error) {
           console.error('Error in bulk action:', error);
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const openApprovalModal = (returnItem, action) => {
    setSelectedReturn({ ...returnItem, action });
    setApprovalNote("");
    setShowModal(true);
  };

  const handleNextPage = () => {
    if (nextPage) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      fetchPendingReturns(newPage, pageSize, searchQuery, startDate, endDate, status);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      fetchPendingReturns(newPage, pageSize, searchQuery, startDate, endDate, status);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchPendingReturns(pageNumber, pageSize, searchQuery, startDate, endDate, status);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending Approval":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "Approved":
        return "bg-green-100 text-green-800 border-green-300";
      case "Rejected":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    fetchPendingReturns(currentPage, pageSize, searchQuery, startDate, endDate, status);
  }, [pageSize, activeSession, status]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1700px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <span>Inventory</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Admin Approval</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Return Approval</h1>
          <p className="text-gray-600 mt-1">Review and approve or reject drug return requests</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-gray-600" />
              <h3 className="text-sm font-semibold text-gray-900">Filter Returns</h3>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="Drug name or invoice..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="Pending Confirmation">Pending Confirmation</option>
                  <option value="Pending Approval">Pending Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Apply Filters
              </button>
              {(searchQuery || startDate || endDate) && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Page Size & Bulk Actions */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Show</span>
              <select
                onChange={(e) => setPageSize(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                value={pageSize}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span className="text-sm text-gray-600">entries</span>
            </div>

            {selectedReturns.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleBulkApprovalAction('approve')}
                  disabled={actionLoading === 'bulk'}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 shadow-sm"
                >
                  <CheckCircle size={16} />
                  Bulk Approve ({selectedReturns.length})
                </button>
                <button
                  onClick={() => handleBulkApprovalAction('reject')}
                  disabled={actionLoading === 'bulk'}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 shadow-sm"
                >
                  <XCircle size={16} />
                  Bulk Reject ({selectedReturns.length})
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading returns...</span>
                </div>
              </div>
            ) : returns?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="py-4 px-6 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={returns?.length > 0 && selectedReturns.length === returns.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedReturns(returns);
                          } else {
                            setSelectedReturns([]);
                          }
                        }}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Drug Name
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Invoice ID
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Initiated By
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Witness
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Qty Returned
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Batch Number
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {returns.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-center">
                        <input
                          type="checkbox"
                          checked={selectedReturns.some(r => r.id === item.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedReturns([...selectedReturns, item]);
                            } else {
                              setSelectedReturns(selectedReturns.filter(r => r.id !== item.id));
                            }
                          }}
                          className="rounded text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {item.drug_name || item.drug || "N/A"}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        #{item.invoice_id || "N/A"}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-900">
                        {item.posted_by || item.initiated_by || "Unknown"}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item.witness || "N/A"}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item.returning_quantity || 0}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 font-mono">
                        {item.batch_number || "N/A"}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {formatDate(item.created_at || item.date)}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                          {item.status || "Unknown"}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          {
                            item.status == "Pending Approval" ? (
                              <div>
                                <button
                                  onClick={() => openApprovalModal(item, 'approve')}
                                  disabled={actionLoading === item.id}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors border border-green-200 disabled:opacity-50"
                                >
                                  <CheckCircle size={14} />
                                  Approve
                                </button>
                                <button
                                  onClick={() => openApprovalModal(item, 'reject')}
                                  disabled={actionLoading === item.id}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors border border-red-200 disabled:opacity-50"
                                >
                                  <XCircle size={14} />
                                  Reject
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => openApprovalModal(item, 'approve')}
                                disabled={actionLoading === item.id}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200 disabled:opacity-50"
                              >
                                <EyeIcon size={14} />
                                Details
                              </button>
                            )
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <Package className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <p className="text-gray-500 text-sm">No returns found</p>
                {(searchQuery || startDate || endDate) && (
                  <p className="text-gray-400 text-xs mt-1">
                    Try adjusting your filters
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {returns?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      {showModal && selectedReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {selectedReturn.action === 'approve' ? 'Approve' : 'Reject'} Return Request
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Review the return details before making your decision
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Return Details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Drug Name</p>
                    <p className="text-sm font-medium text-gray-900">{selectedReturn.drug_name || selectedReturn.drug}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Invoice ID</p>
                    <p className="text-sm font-medium text-gray-900">#{selectedReturn.invoice_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Quantity Returned</p>
                    <p className="text-sm font-medium text-gray-900">{selectedReturn.returning_quantity}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Batch Number</p>
                    <p className="text-sm font-medium text-gray-900">{selectedReturn.batch_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Initiated By</p>
                    <p className="text-sm font-medium text-gray-900">{selectedReturn.posted_by || selectedReturn.initiated_by}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Witness</p>
                    <p className="text-sm font-medium text-gray-900">{selectedReturn.witness || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Date</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(selectedReturn.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Current Status</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedReturn.status)}`}>
                      {selectedReturn.status}
                    </span>
                  </div>
                </div>
                {selectedReturn.return_reason && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Return Reason</p>
                    <p className="text-sm text-gray-700">{selectedReturn.return_reason}</p>
                  </div>
                )}
                {selectedReturn.witness_note && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Witness Note</p>
                    <p className="text-sm text-gray-700">{selectedReturn.witness_note}</p>
                  </div>
                )}
              </div>


            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedReturn(null);
                  setApprovalNote("");
                }}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              {
                selectedReturn?.status == "Pending Approval" && (

                  <button
                    onClick={() => handleApprovalAction(selectedReturn.id, selectedReturn.action)}
                    disabled={actionLoading}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2 ${selectedReturn.action === 'approve'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                  >
                    {actionLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        {selectedReturn.action === 'approve' ? <CheckCircle size={16} /> : <XCircle size={16} />}
                        Confirm {selectedReturn.action === 'approve' ? 'Approval' : 'Rejection'}
                      </>
                    )}
                  </button>
                )
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminItemReturnList;